import { ApolloError } from '@apollo/client';
import { DaysOfWeek, Service } from '@client/graphql/types/graphql.ts';
import { formatGraphQlError, validationErrors } from '@client/module/error/error.ts';
import useCreateSchedule from '@client/page/management/availability-limitation/logic/use-create-schedule.ts';
import SelectDaysOfWeek from '@client/page/management/menu/menu-section-create/component/select-days-of-week.tsx';
import { useFocusedBranchContext } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import AlertError from '@packages/ui/alert/alert-error.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import Spinner from '@packages/ui/spinner.tsx';
import { captureException } from '@sentry/browser';
import { useTranslate } from '@tolgee/react';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import HourDropdown from './hour-dropdown';

interface AvailabilityScheduledData {
  capacity: number;
  message: string;
}

interface TimeRuleData {
  daysOfWeek: DaysOfWeek[];
  startTime: string;
  endTime: string;
}

interface AvailabilityScheduledCreateBatchProps {
  capacity: number;
  daysOfWeek: DaysOfWeek[];
  startTime: string;
  endTime: string;
  message?: string;
  stepDuration: number;
}

function splitTimeRangeToSlots(start: string, end: string, stepDuration: number): { start: string; end: string }[] {
  const slots: { start: string; end: string }[] = [];
  const endMoment = moment(end, 'HH:mm', true);

  const referTime = moment(start, 'HH:mm', true);
  while (referTime.clone().add(stepDuration, 'minutes').isSameOrBefore(endMoment)) {
    slots.push({
      start: referTime.format('HH:mm'),
      end: referTime
        .clone()
        .add(stepDuration - 1, 'minutes')
        .format('HH:mm'),
    });

    referTime.add(stepDuration, 'minutes');
  }

  return slots;
}

export default function AvailabilityScheduledCreateBatch(props: { service: Service; onClose: () => void }) {
  const branchId = useFocusedBranchContext().branch?.id ?? '';
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();
  const [create, { loading, error }] = useCreateSchedule();
  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { errors },
  } = useForm<AvailabilityScheduledCreateBatchProps>({
    defaultValues: {
      capacity: 0,
      daysOfWeek: [],
      startTime: '09:00',
      endTime: '12:00',
      message: '',
      stepDuration: 60,
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const applicationErrors = formatGraphQlError(error?.graphQLErrors);
  const validationError = validationErrors(applicationErrors);
  const onSubmit = async (values: AvailabilityScheduledCreateBatchProps) => {
    const slots = splitTimeRangeToSlots(values.startTime, values.endTime, values.stepDuration);
    if (slots.length === 0) {
      setError('startTime', {
        type: 'manual',
        message: t('availability-schedule.create.no-slot', 'No slot to create'),
      });
      return;
    }
    try {
      await Promise.all(
        slots.map((slot) =>
          create({
            variables: {
              branchId: branchId,
              input: {
                service: props.service,
                capacity: Number(values.capacity),
                message: values.message,
              },
              timeRuleInput: {
                daysOfWeeks: values.daysOfWeek,
                startTime: slot.start,
                endTime: slot.end,
              },
            },
          }),
        ),
      );
      showAlert({
        title: t('availability-schedule.create.success', 'Successful'),
        status: 'success',
        message: t(
          'availability-schedule.create.batch.mess',
          'Batch schedule availability limitation has been created',
        ),
      });
      props.onClose();
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationErrors = formatGraphQlError(err.graphQLErrors);
        const validationError = validationErrors(applicationErrors);
        for (const field in validationError) {
          setError(
            field as keyof (AvailabilityScheduledData | TimeRuleData),
            {
              type: 'server',
              message: t(validationError[field] ?? 'validation-error.common.alert'),
            },
            { shouldFocus: true },
          );
        }
      }
      captureException(err);
    }
  };
  return (
    <Card>
      <CardHeader
        title={t('availability-schedule.create-batch.header', 'Batch create schedule for {service}', {
          service: t(props.service),
        })}
      />
      <CardContent>
        <FormLayout>
          <FormItem className="max-w-lg" title={t('availability-schedule.create.capacity.header')}>
            <Controller
              control={control}
              name="capacity"
              rules={{
                required: t('availability-schedule.create.capacity.validate.required', 'Capacity require number'),
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <TextInput
                    data-testid="title-input"
                    type="number"
                    label="capacity"
                    name="capacity"
                    placeholder={t('availability-schedule.create.capacity.placeholder')}
                    value={value}
                    error={errors.capacity?.message}
                    autoComplete={'off'}
                    onChange={onChange}
                  />
                </>
              )}
            />
          </FormItem>

          <FormItem title={t('availability-schedule.create.range-time.header')}>
            <div className="flex flex-col sm:flex-row gap-4">
              <Controller
                control={control}
                name="startTime"
                rules={{
                  required: t('availability-schedule.create.startTime.validate.required', 'Start time required'),
                  validate: (value, formValues) => {
                    if (value && formValues.endTime) {
                      return (
                        value < formValues.endTime ||
                        t(
                          'availability-schedule.create.endTime.validate.compare',
                          'Start time must be smaller than end time',
                        )
                      );
                    }
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <div>
                    <HourDropdown value={value} onChange={onChange} />
                    {errors.startTime && (
                      <p className="text-red-500 text-sm mt-2 max-w-36">{errors.startTime.message}</p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={control}
                name="endTime"
                rules={{
                  required: t('availability-schedule.create.endTime.validate.required', 'End time required'),
                  validate: (value, formValues) => {
                    if (value && formValues.startTime) {
                      return (
                        formValues.startTime < value ||
                        t(
                          'availability-schedule.create.endTime.validate.compare',
                          'End time must be greater than start time',
                        )
                      );
                    }
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <div>
                    <HourDropdown value={value} onChange={onChange} />
                    {errors.endTime && <p className="text-red-500 text-sm mt-2 max-w-36">{errors.endTime.message}</p>}
                  </div>
                )}
              />
            </div>
          </FormItem>

          <FormItem
            className="max-w-lg"
            title={t('availability-schedule.create.step-duration.header', 'Step duration (minutes)')}
          >
            <Controller
              control={control}
              name="stepDuration"
              rules={{
                required: t(
                  'availability-schedule.create-step-duration.validate.required',
                  'Step duration is required',
                ),
                min: {
                  value: 1,
                  message: t(
                    'availability-schedule.create-step-duration.validate.min',
                    'Step duration must be at least 1 minute',
                  ),
                },
                max: {
                  value: 180,
                  message: t(
                    'availability-schedule.create-step-duration.validate.max',
                    'Step duration must be at most 180 minutes',
                  ),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  type="number"
                  label="stepDuration"
                  name="stepDuration"
                  placeholder={t(
                    'availability-schedule.create-step-duration.placeholder',
                    'Enter step duration (minutes)',
                  )}
                  value={value}
                  error={errors.stepDuration?.message}
                  autoComplete={'off'}
                  onChange={onChange}
                />
              )}
            />
          </FormItem>

          <FormItem className="mt-8" title={t('availability-schedule.create.days-of-week.header')}>
            <Controller
              control={control}
              name="daysOfWeek"
              rules={{
                required: t('availability-schedule.create.dow.validate.required', 'Day of week required'),
              }}
              render={({ field: { onChange, value } }) => <SelectDaysOfWeek value={value} onChange={onChange} />}
            />
            {errors.daysOfWeek && <p className="text-red-500 text-sm mt-2">{errors.daysOfWeek.message}</p>}
          </FormItem>

          <FormItem className="max-w-md" title={t('availability-schedule.create.description.title')}>
            <Controller
              control={control}
              name="message"
              render={({ field: { onChange, value } }) => (
                <textarea
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1
                             ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset
                             focus:ring-indigo-600 sm:text-sm sm:leading-6 min-h-44"
                  defaultValue={value ?? ''}
                  onChange={(newValue) => {
                    onChange(newValue.target.value.trim());
                  }}
                />
              )}
            />
          </FormItem>
        </FormLayout>
      </CardContent>
      {validationError.dayOfWeekConflicts && (
        <AlertError
          title={t('availability.capacity-rule-conflict', 'Capacity rule conflict')}
          message={validationError.dayOfWeekConflicts}
        />
      )}
      <CardFooter>
        <SecondaryButton onClick={props.onClose}>{t('common.button.cancel')}</SecondaryButton>
        <PrimaryButton
          onClick={() => {
            clearErrors();
            handleSubmit(onSubmit)().catch(captureException);
          }}
        >
          {t('availability-schedule.create-batch.button', 'Batch Create')}
        </PrimaryButton>
        {loading && <Spinner />}
      </CardFooter>
    </Card>
  );
}
